import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Play, Lock, CheckCircle, ArrowRight, Rocket, Shield, Clock, Mail, User, Building, Phone } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const DemoVideosPage = () => {
  const navigate = useNavigate();
  const [hasAccess, setHasAccess] = useState(false);
  const [videos, setVideos] = useState([]);
  const [categorizedVideos, setCategorizedVideos] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [activeVideo, setActiveVideo] = useState(null);
  
  // Lead capture form
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    company: '',
    phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Check if user already has access (stored in localStorage)
    const demoAccess = localStorage.getItem('rooftrack_demo_access');
    if (demoAccess) {
      setHasAccess(true);
      loadVideos();
    } else {
      setIsLoading(false);
    }
  }, []);

  const loadVideos = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/demo-videos/public`);
      setVideos(response.data.videos || []);
      setCategorizedVideos(response.data.categorized || {});
    } catch (err) {
      console.error('Error loading videos:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email) {
      setError('Please enter your email address');
      return;
    }
    
    setIsSubmitting(true);
    setError('');
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/demo-videos/access`, formData);
      
      if (response.data.access_granted) {
        localStorage.setItem('rooftrack_demo_access', JSON.stringify({
          email: formData.email,
          lead_id: response.data.lead_id,
          granted_at: new Date().toISOString()
        }));
        setHasAccess(true);
        loadVideos();
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
      console.error('Error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getCategoryTitle = (category) => {
    switch (category) {
      case 'core_feature': return 'Core Features';
      case 'addon_feature': return 'Premium Add-ons';
      case 'onboarding': return 'Getting Started';
      default: return 'Other Videos';
    }
  };

  const getCategoryDescription = (category) => {
    switch (category) {
      case 'core_feature': return 'Essential tools included in every HomeFixer360 subscription';
      case 'addon_feature': return 'Advanced features to supercharge your business';
      case 'onboarding': return 'Quick guides to get you up and running';
      default: return '';
    }
  };

  // Lead Capture Gate
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
        {/* Header */}
        <header className="bg-transparent py-4 px-6">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <div 
              className="text-2xl font-bold text-white cursor-pointer"
              onClick={() => navigate('/')}
            >
              HomeFixer360
            </div>
            <Button 
              variant="outline" 
              className="border-white text-white hover:bg-white hover:text-slate-900"
              onClick={() => navigate('/login')}
            >
              Sign In
            </Button>
          </div>
        </header>

        <div className="flex items-center justify-center px-4 py-12">
          <div className="max-w-4xl w-full">
            {/* Hero Section */}
            <div className="text-center mb-12">
              <div className="inline-flex items-center bg-orange-500/20 text-orange-300 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Play className="w-4 h-4 mr-2" />
                Exclusive Product Demos
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                See HomeFixer360 in Action
              </h1>
              <p className="text-xl text-blue-200 max-w-2xl mx-auto">
                Watch how home service pros are saving 10+ hours per week with our AI-powered satellite estimates and automation tools.
              </p>
            </div>

            {/* Form Card */}
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden max-w-lg mx-auto">
              <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-6 text-white text-center">
                <Lock className="w-10 h-10 mx-auto mb-3" />
                <h2 className="text-xl font-bold">Unlock All Demo Videos</h2>
                <p className="text-orange-100 text-sm mt-1">Enter your details for instant access</p>
              </div>

              <form onSubmit={handleSubmit} className="p-8 space-y-5">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    {error}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Mail className="w-4 h-4 inline mr-2" />
                    Email Address *
                  </label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="you@company.com"
                    required
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <User className="w-4 h-4 inline mr-2" />
                    Your Name
                  </label>
                  <Input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="John Smith"
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Building className="w-4 h-4 inline mr-2" />
                    Company Name
                  </label>
                  <Input
                    type="text"
                    value={formData.company}
                    onChange={(e) => setFormData({...formData, company: e.target.value})}
                    placeholder="ABC Roofing"
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Phone className="w-4 h-4 inline mr-2" />
                    Phone (optional)
                  </label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="(555) 123-4567"
                    className="w-full"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3 text-lg font-semibold"
                >
                  {isSubmitting ? (
                    'Unlocking...'
                  ) : (
                    <>
                      <Play className="w-5 h-5 mr-2" />
                      Watch Demo Videos
                    </>
                  )}
                </Button>

                <p className="text-xs text-gray-500 text-center">
                  By submitting, you agree to receive emails from HomeFixer360. Unsubscribe anytime.
                </p>
              </form>
            </div>

            {/* Trust Indicators */}
            <div className="mt-12 grid grid-cols-3 gap-6 max-w-lg mx-auto">
              <div className="text-center">
                <Rocket className="w-8 h-8 text-orange-400 mx-auto mb-2" />
                <p className="text-white text-sm font-medium">60-Second Estimates</p>
              </div>
              <div className="text-center">
                <Shield className="w-8 h-8 text-orange-400 mx-auto mb-2" />
                <p className="text-white text-sm font-medium">No Credit Card</p>
              </div>
              <div className="text-center">
                <Clock className="w-8 h-8 text-orange-400 mx-auto mb-2" />
                <p className="text-white text-sm font-medium">Instant Access</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Video Player Modal
  const VideoModal = ({ video, onClose }) => (
    <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="max-w-4xl w-full" onClick={e => e.stopPropagation()}>
        <div className="bg-slate-900 rounded-lg overflow-hidden">
          <div className="p-4 border-b border-slate-700 flex justify-between items-center">
            <h3 className="text-white font-semibold">{video.title}</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl">&times;</button>
          </div>
          <div className="aspect-video bg-black">
            {video.video_url ? (
              <video 
                src={video.video_url} 
                controls 
                autoPlay 
                className="w-full h-full"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-400">
                <p>Video coming soon...</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // Videos Available View
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white shadow-sm py-4 px-6">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div 
            className="text-2xl font-bold text-slate-800 cursor-pointer"
            onClick={() => navigate('/')}
          >
            HomeFixer360
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-sm text-green-600 flex items-center">
              <CheckCircle className="w-4 h-4 mr-1" />
              Demo Access Granted
            </span>
            <Button onClick={() => navigate('/register')}>
              Start Free Trial
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Page Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-slate-800 mb-4">Product Demo Videos</h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Explore how HomeFixer360 can transform your home service business. Watch our feature demos and see the platform in action.
          </p>
        </div>

        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full mx-auto"></div>
            <p className="mt-4 text-slate-600">Loading demos...</p>
          </div>
        ) : videos.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl shadow-sm">
            <Play className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-700 mb-2">Demo Videos Coming Soon</h2>
            <p className="text-slate-500 mb-6">We're preparing amazing demo content for you. Check back soon!</p>
            <Button onClick={() => navigate('/register')}>
              Get Started Instead
            </Button>
          </div>
        ) : (
          <>
            {/* Onboarding Videos */}
            {categorizedVideos.onboarding?.length > 0 && (
              <div className="mb-12">
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-slate-800">{getCategoryTitle('onboarding')}</h2>
                  <p className="text-slate-600">{getCategoryDescription('onboarding')}</p>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {categorizedVideos.onboarding.map(video => (
                    <VideoCard 
                      key={video.id} 
                      video={video} 
                      onPlay={() => setActiveVideo(video)}
                      featured
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Core Features */}
            {categorizedVideos.core_feature?.length > 0 && (
              <div className="mb-12">
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-slate-800">{getCategoryTitle('core_feature')}</h2>
                  <p className="text-slate-600">{getCategoryDescription('core_feature')}</p>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {categorizedVideos.core_feature.map(video => (
                    <VideoCard 
                      key={video.id} 
                      video={video} 
                      onPlay={() => setActiveVideo(video)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Add-on Features */}
            {categorizedVideos.addon_feature?.length > 0 && (
              <div className="mb-12">
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-slate-800">{getCategoryTitle('addon_feature')}</h2>
                  <p className="text-slate-600">{getCategoryDescription('addon_feature')}</p>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {categorizedVideos.addon_feature.map(video => (
                    <VideoCard 
                      key={video.id} 
                      video={video} 
                      onPlay={() => setActiveVideo(video)}
                    />
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* CTA Section */}
        <div className="mt-16 bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl p-8 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-orange-100 mb-6 max-w-xl mx-auto">
            Join hundreds of home service pros already saving time and winning more jobs with HomeFixer360.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              className="bg-white text-orange-600 hover:bg-orange-50"
              onClick={() => navigate('/register')}
            >
              Start Free Trial
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-white text-white hover:bg-white/10"
              onClick={() => window.open('https://calendly.com/homefixer360/demo', '_blank')}
            >
              Schedule Live Demo
            </Button>
          </div>
        </div>
      </div>

      {/* Video Modal */}
      {activeVideo && (
        <VideoModal video={activeVideo} onClose={() => setActiveVideo(null)} />
      )}
    </div>
  );
};

// Video Card Component
const VideoCard = ({ video, onPlay, featured = false }) => (
  <div 
    className={`bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group ${featured ? 'ring-2 ring-orange-500' : ''}`}
    onClick={onPlay}
  >
    <div className="aspect-video bg-slate-200 relative">
      {video.thumbnail_url ? (
        <img src={video.thumbnail_url} alt={video.title} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center">
          <Play className="w-16 h-16 text-white/50" />
        </div>
      )}
      
      {/* Play Overlay */}
      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
        <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center">
          <Play className="w-8 h-8 text-orange-600 ml-1" fill="currentColor" />
        </div>
      </div>

      {featured && (
        <div className="absolute top-3 left-3 bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded">
          START HERE
        </div>
      )}

      {video.duration && (
        <div className="absolute bottom-3 right-3 bg-black/70 text-white text-xs px-2 py-1 rounded">
          {Math.floor(video.duration / 60)}:{(video.duration % 60).toString().padStart(2, '0')}
        </div>
      )}
    </div>
    
    <div className="p-4">
      <h3 className="font-semibold text-slate-800 group-hover:text-orange-600 transition-colors">
        {video.title}
      </h3>
      <p className="text-sm text-slate-500 mt-1 line-clamp-2">
        {video.script?.substring(0, 100)}...
      </p>
    </div>
  </div>
);

export default DemoVideosPage;
